import { blynFetch } from './blynFetch';
import { getBlynBackendBase } from './blynApiClient';
import { parseSseBlock, type BackgroundJob, type BackgroundJobKind } from './blynChatService';

/** Resource collection each background job kind's progress stream lives
 * under: GET /api/v1/{collection}/{id}/progress/stream. */
const PROGRESS_COLLECTIONS: Record<BackgroundJobKind, string> = {
  journey: 'journeys',
  campaign: 'campaigns',
};

/** One normalised update from a resource's progress stream. */
export interface JobProgressUpdate {
  status: string;
  message?: string;
  /** 0–100, when the worker reports how far along it is. */
  percent?: number;
  /** 'success' / 'error' once the worker has finished; absent while running. */
  terminal?: 'success' | 'error';
}

export interface WatchJobProgressOptions {
  tenantId?: string;
  onUpdate: (update: JobProgressUpdate) => void;
  /** The watch gave up (repeated connection failures / non-retryable HTTP
   * error) before the job reached a terminal status. */
  onLost?: (error: Error) => void;
}

const SUCCESS_STATUSES = new Set(['completed', 'complete', 'done', 'succeeded', 'success', 'ready', 'active']);
const ERROR_STATUSES = new Set(['failed', 'error', 'errored', 'cancelled', 'canceled']);
const IGNORED_EVENTS = new Set(['heartbeat', 'ping', 'keepalive']);

const MAX_CONSECUTIVE_FAILURES = 5;
const MAX_BACKOFF_MS = 30_000;
// Pause before reopening a stream the server closed cleanly (its time cap),
// so an endpoint that sends one heartbeat then closes can't spin a tight loop.
const RECONNECT_DELAY_MS = 1000;

/** Accepts 0–100, or a fraction below 1 (a finished job is terminal, so a
 * bare 1 reads as 1%); anything else is ignored. */
function toPercent(value: unknown): number | undefined {
  const n = typeof value === 'string' ? parseFloat(value) : value;
  if (typeof n !== 'number' || !Number.isFinite(n) || n < 0) return undefined;
  const pct = n < 1 ? n * 100 : n;
  return pct > 100 ? undefined : Math.round(pct);
}

function normalise(event: string, data: any): JobProgressUpdate | null {
  if (IGNORED_EVENTS.has(event)) return null;
  const payload = data && typeof data === 'object' ? data : {};
  const status = String(payload.status ?? payload.state ?? (event !== 'message' ? event : '') ?? '').toLowerCase();
  const text = typeof data === 'string' ? data : undefined;
  const message =
    payload.message ?? payload.user_message ?? payload.step_label ?? payload.step ?? payload.detail ?? payload.error ?? text;
  const percent = toPercent(payload.percent ?? payload.progress ?? payload.pct);
  if (!status && !message && percent == null) return null;

  let terminal: JobProgressUpdate['terminal'];
  if (ERROR_STATUSES.has(status)) terminal = 'error';
  else if (SUCCESS_STATUSES.has(status)) terminal = 'success';

  return {
    status,
    ...(message && { message: String(message) }),
    ...(percent != null && { percent }),
    ...(terminal && { terminal }),
  };
}

/** Follow a background job (journey / campaign build) until it reaches a
 * terminal status.
 * The backend closes the stream after a time cap (15 min) and expects the
 * client to reconnect, so a stream that ends without a terminal status is
 * reopened; network errors retry with backoff. Returns a stop function —
 * callers must call it on unmount / conversation switch. */
export function watchJobProgress(job: BackgroundJob, options: WatchJobProgressOptions): () => void {
  const { tenantId, onUpdate, onLost } = options;
  let stopped = false;
  let controller: AbortController | null = null;
  let retryTimer: ReturnType<typeof setTimeout> | null = null;
  let failures = 0;

  const url = `${getBlynBackendBase()}/api/v1/${PROGRESS_COLLECTIONS[job.kind]}/${encodeURIComponent(job.id)}/progress/stream`
    + (tenantId ? `?tenant_id=${encodeURIComponent(tenantId)}` : '');

  const stop = () => {
    stopped = true;
    if (retryTimer) clearTimeout(retryTimer);
    retryTimer = null;
    controller?.abort();
    controller = null;
  };

  const scheduleReconnect = (delayMs: number) => {
    if (stopped) return;
    retryTimer = setTimeout(connect, delayMs);
  };

  /** Returns true once a terminal update has been delivered. */
  const handle = (block: string): boolean => {
    if (!block.trim()) return false;
    const parsed = parseSseBlock(block);
    if (!parsed) return false;
    const update = normalise(parsed.event, parsed.data);
    if (!update) return false;
    onUpdate(update);
    return !!update.terminal;
  };

  async function connect() {
    retryTimer = null;
    if (stopped) return;
    controller = new AbortController();
    try {
      const res = await blynFetch(url, { headers: { Accept: 'text/event-stream' }, signal: controller.signal });
      if (!res.ok) {
        // 4xx (other than timeout / rate limit) won't fix itself on retry.
        if (res.status >= 400 && res.status < 500 && res.status !== 408 && res.status !== 429) {
          stop();
          onLost?.(new Error(`Progress stream unavailable (${res.status})`));
          return;
        }
        throw new Error(`Progress stream error ${res.status}`);
      }
      const reader = res.body?.getReader();
      if (!reader) throw new Error('No response body');

      const decoder = new TextDecoder();
      let buffer = '';
      let receivedData = false;
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        receivedData = true;
        failures = 0;
        buffer += decoder.decode(value, { stream: true });
        const blocks = buffer.split(/\n\n/);
        buffer = blocks.pop() ?? '';
        for (const block of blocks) {
          if (handle(block)) { stop(); return; }
        }
      }
      if (handle(buffer)) { stop(); return; }
      // Server closed without a terminal status (time cap) — reconnect. A
      // stream that closed without sending anything counts as a failure so
      // a misbehaving endpoint can't spin a tight reconnect loop.
      if (!receivedData) throw new Error('Progress stream closed without data');
      scheduleReconnect(RECONNECT_DELAY_MS);
    } catch (error) {
      if (stopped || (error instanceof Error && error.name === 'AbortError')) return;
      failures += 1;
      if (failures >= MAX_CONSECUTIVE_FAILURES) {
        stop();
        onLost?.(error instanceof Error ? error : new Error(String(error)));
        return;
      }
      scheduleReconnect(Math.min(1000 * 2 ** (failures - 1), MAX_BACKOFF_MS));
    }
  }

  connect();
  return stop;
}
